import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiArrowUpRight, FiLayers, FiActivity, FiBox, FiCode, FiCheck } from "react-icons/fi";
import SEO from "../components/SEO";
import BlogCard from "../components/BlogCard";
import useReveal from "../hooks/useReveal";
import { getAllPosts } from "../data/blog";
import { SITE } from "../config/site";

const SERVICE_ICONS = [FiLayers, FiActivity, FiBox, FiCode];
const SERVICE_LINKS = ["/hizmetler#statik", "/hizmetler#deprem", "/hizmetler#bim", "/yazilimlar"];

const PROJECT_IMAGES = [
  "/img/portfolio/topkapiluxera.png",
  "/img/portfolio/zadar.jpg",
  "/img/portfolio/cyriptocarbon.png",
  "/img/portfolio/afifahmetpasayalisi.png",
  "/img/portfolio/etex.png",
  "/img/portfolio/netex.png",
];

/* ---- Hero visual: first-mode oscillation of a framed building ----
   Columns bend through a smooth cantilever mode shape, floor slabs follow,
   nodes pulse and glow, triangular seismic load pulses in phase.
   All colours are CSS-driven (currentColor / var) → theme adaptive.        */
const NF = 10;
const TOP = 80, BOT = 452, LX = 132, RX = 276, CX = (LX + RX) / 2;
const LEVELS = Array.from({ length: NF }, (_, k) => {
  const h = (k + 1) / NF;
  return { h, y: BOT - h * (BOT - TOP) };
});
const FORCES = [3, 5, 7, 9].map((i) => {
  const h = i / NF;
  const y = BOT - h * (BOT - TOP);
  const len = 16 + h * 52;
  return { y, x1: LX - 8 - len, x2: LX - 8 };
});

function ModalFrame() {
  const ref = useRef(null);
  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const colL = svg.querySelector(".col-l");
    const colR = svg.querySelector(".col-r");
    const floors = svg.querySelectorAll(".fl");
    const nodes = svg.querySelectorAll(".nd");
    const forces = svg.querySelectorAll(".force");
    const phi = (h) => 1 - Math.cos((h * Math.PI) / 2);
    let t = 0, raf = 0;
    const tick = () => {
      t += 0.045;
      const amp = 22 * (0.7 + 0.3 * Math.sin(t * 0.28));
      const phase = Math.sin(t);
      let dl = `M ${LX} ${BOT}`, dr = `M ${RX} ${BOT}`;
      LEVELS.forEach((lv, i) => {
        const dx = amp * phi(lv.h) * phase;
        dl += ` L ${(LX + dx).toFixed(1)} ${lv.y.toFixed(1)}`;
        dr += ` L ${(RX + dx).toFixed(1)} ${lv.y.toFixed(1)}`;
        const fl = floors[i];
        if (fl) { fl.setAttribute("x1", (LX + dx).toFixed(1)); fl.setAttribute("x2", (RX + dx).toFixed(1)); fl.setAttribute("y1", lv.y); fl.setAttribute("y2", lv.y); }
        const nd = nodes[i];
        if (nd) {
          const pulse = 0.5 + 0.5 * Math.sin(t * 2 - i * 0.55);
          nd.setAttribute("cx", (CX + dx).toFixed(1));
          nd.setAttribute("cy", lv.y);
          nd.setAttribute("r", (2.4 + pulse * 1.3).toFixed(2));
          nd.setAttribute("opacity", (0.5 + pulse * 0.5).toFixed(2));
        }
      });
      colL.setAttribute("d", dl);
      colR.setAttribute("d", dr);
      const fo = (0.22 + Math.max(0, phase) * 0.78).toFixed(2);
      forces.forEach((f) => f.setAttribute("opacity", fo));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <svg className="msvg" ref={ref} viewBox="0 0 360 520" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
      {/* undeformed reference */}
      <line className="ghost" x1={LX} y1={BOT} x2={LX} y2={TOP} />
      <line className="ghost" x1={RX} y1={BOT} x2={RX} y2={TOP} />
      <line className="ghost" x1={CX} y1={BOT} x2={CX} y2={TOP} />
      {/* ground + foundation */}
      <line className="base" x1="34" y1={BOT} x2="326" y2={BOT} />
      <text className="lbl" x="34" y={BOT + 16}>±0.00 · ZEMİN</text>
      {/* seismic load arrows */}
      {FORCES.map((f, i) => (
        <g className="force" key={i}>
          <line x1={f.x1} y1={f.y} x2={f.x2} y2={f.y} />
          <polygon points={`${f.x2},${f.y} ${f.x2 - 6},${f.y - 4} ${f.x2 - 6},${f.y + 4}`} />
        </g>
      ))}
      {/* deforming columns */}
      <path className="col col-l" d="" />
      <path className="col col-r" d="" />
      {/* floor slabs */}
      {LEVELS.map((lv, i) => <line className="fl" key={i} x1={LX} x2={RX} y1={lv.y} y2={lv.y} />)}
      {/* glowing floor nodes */}
      <g className="nodes">
        {LEVELS.map((lv, i) => <circle className="nd" key={i} cx={CX} cy={lv.y} r="3" />)}
      </g>
      <text className="lbl-teal" x={CX - 26} y={TOP - 10}>MOD 1 · T = 0.84s</text>
    </svg>
  );
}

/* Seismograph waveform (draws when scrolled into view) — inside a dark band */
function Seismograph() {
  const pathRef = useRef(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    const path = pathRef.current, wrap = wrapRef.current;
    if (!path || !wrap) return;
    const W = 600, MID = 90, N = 240;
    const env = (i) => {
      const x = i / N;
      if (x < 0.18) return (x / 0.18) * 0.6;
      if (x < 0.55) return 0.6 + (Math.sin((x - 0.18) * 12) + 1) * 0.2;
      return Math.max(0, 1 - (x - 0.55) / 0.45);
    };
    const data = [];
    for (let i = 0; i < N; i++) {
      const e = env(i);
      data.push((Math.sin(i * 0.4) + Math.sin(i * 0.13 + 1.5) * 0.6 + Math.sin(i * 0.8 + 0.4) * 0.3) * e);
    }
    let prog = 0, raf = 0, timer = 0, stopped = false;
    const draw = () => {
      if (stopped) return;
      prog = Math.min(prog + 1, N);
      let d = "";
      for (let i = 0; i <= prog; i++) d += (i === 0 ? "M" : "L") + ((i / N) * W).toFixed(1) + " " + (MID - data[i] * 66).toFixed(1) + " ";
      path.setAttribute("d", d);
      if (prog < N) raf = requestAnimationFrame(draw);
      else timer = window.setTimeout(() => { prog = 0; draw(); }, 1600);
    };
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { draw(); io.disconnect(); } }));
    io.observe(wrap);
    return () => { stopped = true; cancelAnimationFrame(raf); window.clearTimeout(timer); io.disconnect(); };
  }, []);
  return (
    <div className="seismo" ref={wrapRef}>
      <svg viewBox="0 0 600 180" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="90" x2="600" y2="90" stroke="rgba(234,241,247,0.18)" strokeWidth="0.5" strokeDasharray="4 5" />
        <path ref={pathRef} d="" fill="none" stroke="#2dd4bf" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

/* Count-up number that animates once when scrolled into view */
function CountUp({ end, duration = 1500 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVal(end); return; }
    let raf = 0, start = 0;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(end * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { raf = requestAnimationFrame(step); io.disconnect(); }
    }), { threshold: 0.4 });
    io.observe(el);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [end, duration]);
  return <span ref={ref}>{val}</span>;
}

/* Interactive glowing structural node-mesh behind the hero (canvas) */
function HeroNodes() {
  const cvsRef = useRef(null);
  useEffect(() => {
    const canvas = cvsRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mouse = { x: -9999, y: -9999 };
    let w = 0, h = 0, nodes = [], raf = 0, stopped = false;

    const init = () => {
      const r = parent.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = w + "px"; canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.max(28, Math.min(78, Math.floor((w * h) / 15000)));
      nodes = [];
      for (let i = 0; i < n; i++) nodes.push({ x: Math.random() * w, y: Math.random() * h, vx: (Math.random() - 0.5) * 0.32, vy: (Math.random() - 0.5) * 0.32 });
    };
    const frame = () => {
      if (stopped) return;
      const light = document.documentElement.getAttribute("data-theme") === "light";
      const linkRGB = light ? "13,148,136" : "45,212,191";
      const linkMax = light ? 0.55 : 0.34;
      const nodeFill = light ? "rgba(13,148,136,0.95)" : "rgba(45,212,191,0.95)";
      const nodeR = light ? 2.1 : 1.7;
      const mouseRGB = light ? "15,118,110" : "94,234,212";
      const MR = 200;
      ctx.clearRect(0, 0, w, h);
      for (const p of nodes) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        const dx = mouse.x - p.x, dy = mouse.y - p.y, d = Math.hypot(dx, dy);
        if (d < 155 && d > 0) { const f = (1 - d / 155) * 1.7; p.x -= (dx / d) * f; p.y -= (dy / d) * f; }
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j], dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 124) {
            ctx.strokeStyle = `rgba(${linkRGB},${(1 - dist / 124) * linkMax})`;
            ctx.lineWidth = light ? 0.9 : 0.7;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      for (const p of nodes) {
        const d = Math.hypot(mouse.x - p.x, mouse.y - p.y);
        if (d < MR) {
          ctx.strokeStyle = `rgba(${mouseRGB},${(1 - d / MR) * (light ? 0.7 : 0.55)})`;
          ctx.lineWidth = light ? 1.2 : 0.9;
          ctx.beginPath(); ctx.moveTo(mouse.x, mouse.y); ctx.lineTo(p.x, p.y); ctx.stroke();
        }
        ctx.fillStyle = nodeFill;
        ctx.beginPath(); ctx.arc(p.x, p.y, nodeR, 0, Math.PI * 2); ctx.fill();
      }
      if (mouse.x > -9000) {
        ctx.strokeStyle = `rgba(${mouseRGB},${light ? 0.55 : 0.45})`;
        ctx.lineWidth = 1.3;
        ctx.beginPath(); ctx.arc(mouse.x, mouse.y, 5.5, 0, Math.PI * 2); ctx.stroke();
      }
      raf = requestAnimationFrame(frame);
    };
    const onMove = (e) => { const r = canvas.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };
    const onResize = () => init();

    init();
    if (reduce) { frame(); stopped = true; }
    else {
      frame();
      window.addEventListener("resize", onResize);
      window.addEventListener("mousemove", onMove, { passive: true });
      parent.addEventListener("mouseleave", onLeave);
    }
    return () => {
      stopped = true; cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
    };
  }, []);
  return <canvas className="hero-canvas" ref={cvsRef} aria-hidden="true" />;
}

export default function Home() {
  const { t } = useTranslation();
  const pageRef = useRef(null);
  useReveal(pageRef, [t]);

  const stats = t("home.stats", { returnObjects: true });
  const marquee = t("home.marquee", { returnObjects: true });
  const services = t("home.services.items", { returnObjects: true });
  const points = t("home.approach.points", { returnObjects: true });
  const values = t("home.approach.values", { returnObjects: true });
  const projects = t("home.projects.items", { returnObjects: true });
  const etexFeatures = t("home.software.etex_features", { returnObjects: true });
  const events = t("home.timeline.events", { returnObjects: true });
  const latestPosts = getAllPosts().slice(0, 3);

  const webSiteLd = { "@context": "https://schema.org", "@type": "WebSite", name: SITE.name, url: SITE.url };

  return (
    <div ref={pageRef}>
      <SEO title={t("home.meta_title")} description={t("home.meta_desc")} jsonLd={webSiteLd} />

      {/* ============ HERO ============ */}
      <section className="hero blueprint">
        <div className="hero-bg" aria-hidden="true" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/img/hero-bg.png)` }} />
        <HeroNodes />
        <div className="container hero-grid">
          <div className="hero-copy">
            <div className="hero-badges reveal">
              <span className="eyebrow">{t("home.hero.badge")}</span>
              <span className="pill teal">{t("home.hero.tag")}</span>
            </div>
            <h1 className="hero-title reveal d1">
              {t("home.hero.title_1")}<br />
              <span className="it">{t("home.hero.title_2")}</span>{" "}
              <span className="accent">{t("home.hero.title_hl")}</span>
            </h1>
            <p className="hero-lede lede reveal d2">{t("home.hero.lede")}</p>
            <div className="hero-actions reveal d3">
              <Link to="/hizmetler" className="btn lg">{t("home.hero.cta_primary")} <FiArrowRight className="arrow" /></Link>
              <Link to="/blog" className="btn ghost lg">{t("home.hero.cta_secondary")}</Link>
            </div>
            <div className="hero-stats reveal d4">
              {stats.map((s, i) => (
                <div className="stat" key={i}>
                  <div className="num"><CountUp end={parseInt(s.num, 10) || 0} /><span className="accent">{s.sup}</span></div>
                  <div className="lbl">{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-visual reveal d2">
            <div className="panel">
              <div className="panel-bar">
                <span>{t("home.hero.panel_title")}</span>
                <span className="live">{t("home.hero.panel_live")}</span>
              </div>
              <div className="panel-stage"><ModalFrame /></div>
              <div className="panel-foot">
                <div><span className="k">{t("home.hero.panel_type_k")}</span><span className="v">{t("home.hero.panel_type_v")}</span></div>
                <div><span className="k">{t("home.hero.panel_perf_k")}</span><span className="v accent">{t("home.hero.panel_perf_v")}</span></div>
                <div><span className="k">{t("home.hero.panel_drift_k")}</span><span className="v">{t("home.hero.panel_drift_v")}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="marquee">
          <div className="marquee-track">
            {[0, 1].map((dup) => (
              <span key={dup} aria-hidden={dup === 1}>
                {marquee.map((m, i) => (
                  <React.Fragment key={i}>{m}<span className="sep">/</span></React.Fragment>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="section" id="services">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow reveal">{t("home.services.eyebrow")}</span>
              <h2 className="sh-title reveal d1">{t("home.services.title")}</h2>
            </div>
            <p className="sh-intro reveal d2">{t("home.services.intro")}</p>
          </div>
          <div className="svc-grid">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Link to={SERVICE_LINKS[i]} className="svc-card card hover reveal" style={{ transitionDelay: `${i * 0.06}s` }} key={i}>
                  <div className="svc-top">
                    <span className="svc-icon"><Icon /></span>
                    <span className="svc-num">{s.num}</span>
                  </div>
                  <h3 className="svc-name">{s.name}</h3>
                  <p className="svc-desc">{s.desc}</p>
                  <span className="svc-arrow"><FiArrowUpRight /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ APPROACH ============ */}
      <section className="section soft approach">
        <div className="container">
          <div className="approach-grid">
            <div className="approach-lhs">
              <span className="eyebrow reveal">{t("home.approach.eyebrow")}</span>
              <h2 className="h-lg reveal d1" style={{ marginTop: 16 }}>
                {t("home.approach.title").split("+")[0]}<span className="accent">+</span>{t("home.approach.title").split("+")[1]}
              </h2>
              <p className="muted reveal d2" style={{ marginTop: 18, maxWidth: 340 }}>{t("home.approach.intro")}</p>
              <ul className="value-list reveal d3">
                {values.map((v, i) => <li key={i}><FiCheck /> {v}</li>)}
              </ul>
            </div>
            <div className="approach-rhs">
              {points.map((p, i) => (
                <p className="manifesto reveal" style={{ transitionDelay: `${i * 0.08}s` }} key={i}>
                  <span className="strike">{p.strike}</span><br />{p.rest}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ EARTHQUAKE ============ */}
      <section className="section ink eq">
        <div className="blueprint-on-ink" style={{ position: "absolute", inset: 0, opacity: 0.5 }} />
        <div className="container eq-grid">
          <div className="eq-copy">
            <span className="eyebrow reveal">{t("home.earthquake.eyebrow")}</span>
            <h2 className="h-lg reveal d1" style={{ marginTop: 16 }}>
              {t("home.earthquake.title_1")}<br /><span className="accent">{t("home.earthquake.title_hl")}</span>
            </h2>
            <p className="reveal d2" style={{ marginTop: 20, color: "var(--on-ink-mute)", maxWidth: 460, lineHeight: 1.7 }}>
              {t("home.earthquake.desc")}
            </p>
            <Link to="/hizmetler#deprem" className="btn on-ink reveal d3" style={{ marginTop: 26 }}>
              {t("home.earthquake.cta")} <FiArrowRight className="arrow" />
            </Link>
          </div>
          <div className="eq-vis reveal d2">
            <Seismograph />
            <div className="eq-stats">
              <span>{t("home.earthquake.stat_pga")}</span>
              <span>{t("home.earthquake.stat_mw")}</span>
              <span>{t("home.earthquake.stat_dur")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROJECTS ============ */}
      <section className="section" id="projects">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow reveal">{t("home.projects.eyebrow")}</span>
              <h2 className="sh-title reveal d1">{t("home.projects.title")}</h2>
            </div>
            <p className="sh-intro reveal d2">{t("home.projects.intro")}</p>
          </div>
          <div className="proj-grid">
            {projects.map((p, i) => (
              <div className={`proj-card reveal${i === 0 ? " big" : ""}`} style={{ transitionDelay: `${i * 0.05}s` }} key={i}>
                <img src={PROJECT_IMAGES[i]} alt={p.title} loading="lazy" />
                <div className="proj-meta">
                  <div className="proj-top"><span>{p.year} · {p.cat}</span><span>{p.loc}</span></div>
                  <h4>{p.title}</h4>
                  <span className="proj-role">{p.role}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 48 }}>
            <Link to="/yazilimlar" className="btn ghost">{t("home.projects.cta")} <FiArrowRight className="arrow" /></Link>
          </div>
        </div>
      </section>

      {/* ============ SOFTWARE ============ */}
      <section className="section mute software">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow reveal">{t("home.software.eyebrow")}</span>
              <h2 className="sh-title reveal d1">{t("home.software.title_1")} <span className="accent">{t("home.software.title_hl")}</span></h2>
            </div>
            <p className="sh-intro reveal d2">{t("home.software.intro")}</p>
          </div>
          <div className="sw-grid">
            <div className="sw-featured card reveal">
              <div className="sw-featured-body">
                <span className="pill teal">{t("home.software.etex_badge")}</span>
                <h3 className="sw-name">E<span className="accent">tex</span></h3>
                <p className="muted">{t("home.software.etex_desc")}</p>
                <ul className="sw-features">
                  {etexFeatures.map((f, i) => <li key={i}><FiCheck /> {f}</li>)}
                </ul>
                <Link to="/etex" className="btn sm">{t("home.software.etex_cta")} <FiArrowRight className="arrow" /></Link>
              </div>
              <div className="sw-featured-img">
                <img src="/img/softwares/etex.png" alt="Etex" loading="lazy" />
              </div>
            </div>

            <div className="sw-side">
              <div className="sw-netex card reveal d1">
                <span className="pill">{t("home.software.netex_badge")}</span>
                <h3 className="sw-name" style={{ fontSize: 32 }}>Netex</h3>
                <p className="muted" style={{ fontSize: 14 }}>{t("home.software.netex_desc")}</p>
                <Link to="/yazilimlar#netex" className="link-arrow">{t("home.software.netex_cta")} <FiArrowRight className="arrow" /></Link>
              </div>
              <div className="sw-netex card reveal d2">
                <span className="pill">{t("home.software.beton_badge")}</span>
                <h3 className="sw-name" style={{ fontSize: 32 }}>{t("home.software.beton_name")}</h3>
                <p className="muted" style={{ fontSize: 14 }}>{t("home.software.beton_desc")}</p>
                <Link to="/yazilimlar#beton" className="link-arrow">{t("home.software.beton_cta")} <FiArrowRight className="arrow" /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TIMELINE ============ */}
      <section className="section timeline-sec">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow reveal">{t("home.timeline.eyebrow")}</span>
              <h2 className="sh-title reveal d1">{t("home.timeline.title_1")} <span className="accent">{t("home.timeline.title_hl")}</span></h2>
            </div>
            <p className="sh-intro reveal d2">{t("home.timeline.intro")}</p>
          </div>
          <div className="timeline">
            {events.map((ev, i) => (
              <div className={`tl-event reveal${i === events.length - 1 ? " major" : ""}`} style={{ transitionDelay: `${i * 0.05}s` }} key={i}>
                <div className="tl-year">{ev.year}</div>
                <div className="tl-dot" />
                <div className="tl-title">{ev.title}</div>
                <div className="tl-desc">{ev.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BLOG PREVIEW ============ */}
      {latestPosts.length > 0 && (
        <section className="section soft">
          <div className="container">
            <div className="section-head">
              <div>
                <span className="eyebrow reveal">{t("home.blog.eyebrow")}</span>
                <h2 className="sh-title reveal d1">{t("home.blog.title")}</h2>
              </div>
              <p className="sh-intro reveal d2">{t("home.blog.intro")}</p>
            </div>
            <div className="blog-grid">
              {latestPosts.map((p, i) => (
                <div className="reveal" style={{ transitionDelay: `${i * 0.06}s` }} key={p.slug}>
                  <BlogCard post={p} />
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 44 }}>
              <Link to="/blog" className="btn ghost">{t("home.blog.cta")} <FiArrowRight className="arrow" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* ============ CTA ============ */}
      <section className="section ink cta-band">
        <div className="blueprint-on-ink" style={{ position: "absolute", inset: 0, opacity: 0.4 }} />
        <div className="container cta-inner">
          <h2 className="h-xl reveal">{t("home.cta.title_1")} <span className="accent">{t("home.cta.title_hl")}</span></h2>
          <div className="cta-row reveal d1">
            <p>{t("home.cta.desc")}</p>
            <div className="cta-actions">
              <Link to="/iletisim" className="btn on-ink">{t("home.cta.primary")} <FiArrowRight className="arrow" /></Link>
              <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="btn ghost on-ink">{t("home.cta.secondary")}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
